import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collaborationApi, type DirectoryEntry } from '../../api/collaboration';

export function MemberDirectoryPage() {
  const [members, setMembers] = useState<DirectoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [scopeType, setScopeType] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const snap = await collaborationApi.directory(scopeType ? { scope_type: scopeType } : undefined);
      setMembers(snap.members);
      setTotal(snap.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load directory');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [scopeType]);

  return (
    <div className="mx-auto max-w-6xl p-6">
      <div className="flex flex-wrap justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Member Directory</h1>
          <p className="mt-1 text-sm text-slate-600">Workspace and organization members across your tenant.</p>
        </div>
        <div className="flex gap-3 text-sm">
          <Link to="/members/search" className="text-slate-700 hover:underline">Search members</Link>
          <Link to="/members/manage" className="text-slate-700 hover:underline">Manage memberships</Link>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-3">
        <select value={scopeType} onChange={(e) => setScopeType(e.target.value)} className="rounded-lg border px-3 py-2 text-sm">
          <option value="">All scopes</option>
          <option value="tenant">Tenant</option>
          <option value="organization">Organization</option>
          <option value="workspace">Workspace</option>
        </select>
        <button type="button" onClick={() => void load()} className="rounded-lg border px-4 py-2 text-sm">Refresh</button>
      </div>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {loading ? <p className="mt-6">Loading…</p> : (
        <>
          <p className="mt-4 text-sm text-slate-500">{total} member{total === 1 ? '' : 's'}</p>
          <div className="mt-4 overflow-x-auto rounded-xl border bg-white">
            <table className="min-w-full text-sm">
              <thead className="border-b bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Member</th>
                  <th className="px-4 py-3 font-medium">Scope</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((entry) => (
                  <tr key={entry.membership.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <p className="font-medium">{entry.display_name || entry.membership.identity_id}</p>
                      {entry.email && <p className="text-slate-500">{entry.email}</p>}
                    </td>
                    <td className="px-4 py-3 capitalize">{entry.membership.scope.type}</td>
                    <td className="px-4 py-3 capitalize">{entry.membership.status}</td>
                    <td className="px-4 py-3">
                      <Link to={`/members/${entry.membership.id}`} className="text-slate-700 hover:underline">Details</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {members.length === 0 && <p className="p-6 text-slate-500">No members found.</p>}
          </div>
        </>
      )}
    </div>
  );
}
